import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { CONTACT } from '../mock';
import { useToast } from '../hooks/use-toast';

export default function Contact() {
  const { toast } = useToast();
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', type: 'Brand', message: '' });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please fill all fields', description: 'We need your name, email and a short message.' });
      return;
    }
    setSent(true);
    toast({ title: 'Message sent!', description: 'Our team will get back within 24 hours.' });
    setTimeout(() => setSent(false), 3500);
    setForm({ name: '', email: '', type: 'Brand', message: '' });
  };

  return (
    <section id="contact" className="relative py-20 md:py-28 bg-[#162028] text-[#ede2c4]">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#ddc390] mb-4 flex items-center gap-2">
              <span className="h-px w-8 bg-[#ddc390]" /> Contacts
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight text-[#ede2c4]">
              Let&rsquo;s make <span className="italic text-[#ddc390]">something</span> unforgettable.
            </h2>
            <div className="mt-10 space-y-5">
              <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-start gap-4 group">
                <span className="h-11 w-11 shrink-0 rounded-full border border-[#ede2c4]/20 flex items-center justify-center group-hover:bg-[#ddc390] group-hover:text-[#162028] group-hover:border-transparent transition-colors">
                  <Phone size={17} />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#ede2c4]/50">Phone</div>
                  <div className="text-[17px] font-medium">{CONTACT.phone}</div>
                </div>
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-start gap-4 group">
                <span className="h-11 w-11 shrink-0 rounded-full border border-[#ede2c4]/20 flex items-center justify-center group-hover:bg-[#ddc390] group-hover:text-[#162028] group-hover:border-transparent transition-colors">
                  <Mail size={17} />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#ede2c4]/50">Email</div>
                  <div className="text-[17px] font-medium">{CONTACT.email}</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <span className="h-11 w-11 shrink-0 rounded-full border border-[#ede2c4]/20 flex items-center justify-center">
                  <MapPin size={17} />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#ede2c4]/50">Studio</div>
                  <div className="text-[15px] leading-relaxed">{CONTACT.address}<br />{CONTACT.city}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-3xl border border-[#ede2c4]/15 bg-[#ede2c4]/[0.03] p-6 md:p-10 backdrop-blur-sm">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-[#ede2c4]/50 mb-2">Your Name</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} type="text" placeholder="Alex Kumar" className="w-full bg-transparent border-b border-[#ede2c4]/25 py-3 text-[16px] text-[#ede2c4] placeholder:text-[#ede2c4]/30 focus:outline-none focus:border-[#ddc390] transition-colors" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-[#ede2c4]/50 mb-2">Email</label>
                  <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="you@studio.com" className="w-full bg-transparent border-b border-[#ede2c4]/25 py-3 text-[16px] text-[#ede2c4] placeholder:text-[#ede2c4]/30 focus:outline-none focus:border-[#ddc390] transition-colors" />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-[11px] uppercase tracking-[0.22em] text-[#ede2c4]/50 mb-3">I am reaching out for</label>
                <div className="flex flex-wrap gap-2">
                  {['Brand', 'Movie', 'Event', 'Other'].map((t) => (
                    <button type="button" key={t} onClick={() => setForm({ ...form, type: t })} className={`px-4 py-2 rounded-full text-[13px] font-medium border transition-colors ${form.type === t ? 'bg-[#ddc390] border-[#ddc390] text-[#162028]' : 'border-[#ede2c4]/25 text-[#ede2c4]/80 hover:border-[#ede2c4]/60'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-[11px] uppercase tracking-[0.22em] text-[#ede2c4]/50 mb-2">Tell us about your project</label>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} placeholder="A quick brief, timelines and dream outcomes..." className="w-full bg-transparent border-b border-[#ede2c4]/25 py-3 text-[16px] text-[#ede2c4] placeholder:text-[#ede2c4]/30 focus:outline-none focus:border-[#ddc390] transition-colors resize-none" />
              </div>

              <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
                <div className="text-[12px] text-[#ede2c4]/50">We reply within 24 hours — promise.</div>
                <button type="submit" disabled={sent} className="inline-flex items-center gap-2 rounded-full bg-[#ddc390] px-6 py-3.5 text-[13.5px] font-semibold text-[#162028] hover:bg-[#ede2c4] transition-colors disabled:opacity-70">
                  {sent ? (<><CheckCircle2 size={16} /> Sent</>) : (<>Send Message <Send size={15} /></>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
